import { ApolloClient, InMemoryCache, type StoreObject } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { createUploadLink } from 'apollo-upload-client';
import { OperationTypeNode } from 'graphql';

import {
  authContextTypePolicies,
  broadcastsRelayPaginationPolicy,
  broadcastAudienceEntriesRelayPaginationPolicy,
  bookingGroupAuditLogsRelayPaginationPolicy,
  clientsRelayPaginationPolicy,
  employeesRelayPaginationPolicy,
  businessLocationsRelayPaginationPolicy,
  loyaltyTransactionsRelayPaginationPolicy,
  clientAutomationTypePolicies,
  platformActivityFeedPolicy,
  platformImpersonationActionsRelayPaginationPolicy,
  platformImpersonationSessionsRelayPaginationPolicy,
  platformOrganizationsRelayPaginationPolicy,
  platformUsersRelayPaginationPolicy,
  productMovementsRelayPaginationPolicy,
  productsRelayPaginationPolicy,
  shiftsRelayPaginationPolicy,
  paymentJournalRelayPaginationPolicy,
  paymentTypePolicies,
  orderTypePolicies,
  showcaseProductsRelayPaginationPolicy,
  stockBalancesRelayPaginationPolicy,
  stockBalanceTypePolicies,
  tireServiceTypePolicies,
  stockMovementsRelayPaginationPolicy,
  tireStoragesRelayPaginationPolicy,
  tireWorkOrdersRelayPaginationPolicy,
  radioTypePolicies,
} from '@/app/providers/apollo-cache-policies';
import router from '@/app/providers/router';
import {
  getHttpStatus,
  isAuthenticationError,
  isBasicAuthChallenge,
  isLocationRequiredError,
  isOrganizationNotFoundError,
  reloadForBasicAuthChallenge,
  reloadForLocationSelection,
  reloadWithoutStaleTenant,
  useAuthStore,
} from '@/entities/auth';
import { handleSessionExpired } from '@/features/session';
import { captureDataRefreshFeedbackFailure, OPERATION_LOCATION_HEADER, setApolloRuntimeClient } from '@/shared/api';
import { resolveApiBaseUrl, useLocationStorage, useOrganizationStorage } from '@/shared/config';
import { useToast } from '@/shared/lib/toast';
import {
  CLIENT_VERSION,
  CLIENT_VERSION_HEADER,
  MAINTENANCE_HEADER,
  reportMaintenance,
  requireAppUpdate,
} from '@shared/app-update';

/**
 * Читает XSRF-TOKEN из cookie для защиты от CSRF атак (требуется для Laravel Sanctum)
 */
function getXsrfToken(): string | null {
  const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

const GRAPHQL_URI = `${resolveApiBaseUrl()}/graphql`;

const httpLink = createUploadLink({
  uri: GRAPHQL_URI,
  credentials: 'include',
});

const authLink = setContext((_, context) => {
  const authStore = useAuthStore();
  const headers = context.headers as Record<string, string> | undefined;
  // Операция записи другой локации задаёт её явно (`operationLocationContext`).
  const operationLocationId = headers?.[OPERATION_LOCATION_HEADER];
  const newHeaders: Record<string, string> = {
    ...headers,
    'X-Requested-With': 'XMLHttpRequest',
    [CLIENT_VERSION_HEADER]: String(CLIENT_VERSION),
    Accept: 'application/json',
  };

  // Добавляем XSRF-TOKEN для Sanctum
  const xsrfToken = getXsrfToken();
  if (xsrfToken) {
    newHeaders['X-XSRF-TOKEN'] = xsrfToken;
  }

  if (context.platform === true || context.omitTenantHeaders === true) {
    delete newHeaders['X-Location-Id'];
    delete newHeaders['X-Organization-Id'];
  } else {
    // Добавляем X-Location-Id или X-Organization-Id для мультитенантности
    const locationId = operationLocationId || useLocationStorage().getLocationId() || authStore.currentLocation?.id;

    if (locationId) {
      newHeaders['X-Location-Id'] = locationId;
    } else {
      const organizationId = useOrganizationStorage().getOrganizationId() || authStore.currentOrganization?.id;
      if (organizationId) {
        newHeaders['X-Organization-Id'] = organizationId;
      }
    }
  }

  return { headers: newHeaders };
});

const isMaintenanceError = (networkError: Error | null | undefined): boolean =>
  networkError instanceof Error &&
  'response' in networkError &&
  networkError.response instanceof Response &&
  networkError.response.headers.get(MAINTENANCE_HEADER) === '1';

const hasAuthenticationNetworkError = (networkError: Error | null | undefined): boolean => {
  const status = getHttpStatus(networkError);

  return status === 401 || status === 419;
};

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (isBasicAuthChallenge(networkError)) {
    reloadForBasicAuthChallenge(GRAPHQL_URI);
    return;
  }

  // Техработы: вместо тоста на каждый запрос экран «Одну минуту» до возвращения сервиса.
  if (isMaintenanceError(networkError)) {
    const definition = getMainDefinition(operation.query);
    reportMaintenance('operation' in definition && definition.operation === OperationTypeNode.MUTATION);
    return;
  }

  const outdated = graphQLErrors?.find(({ extensions }) => extensions?.code === 'CLIENT_OUTDATED');
  if (outdated) {
    requireAppUpdate(outdated.extensions?.minimumClientVersion);
    return;
  }

  if (isOrganizationNotFoundError(networkError)) {
    reloadWithoutStaleTenant();
    return;
  }

  if (operation.getContext().sessionBootstrap === true) {
    return;
  }

  if (graphQLErrors?.some(isAuthenticationError) || hasAuthenticationNetworkError(networkError)) {
    if (getHttpStatus(networkError) === 401) {
      // eslint-disable-next-line no-console -- Диагностика fallback доступна в браузере на уровне Verbose.
      console.debug('[auth] 401 без WWW-Authenticate, трактуем как истёкшую сессию');
    }

    handleSessionExpired(router);
    return;
  }

  if (isLocationRequiredError(networkError)) {
    reloadForLocationSelection();
    return;
  }

  const authorizationError = graphQLErrors?.find(({ extensions }) => extensions?.category === 'authorization');
  if (authorizationError) {
    const toast = useToast();
    const authStore = useAuthStore();

    if (authorizationError.extensions?.guards && Array.isArray(authorizationError.extensions.guards)) {
      authStore.clearContext();
      void router.push('/auth/location-switch');
      toast.error('Необходимо выбрать локацию');
      return;
    }

    toast.error('Недостаточно прав для выполнения операции');
    return;
  }

  // Чувствительные операции (например, push: endpoint/ключи в input и ответ
  // провайдера) обрабатывают ошибки локально, без глобальных toast/redirect.
  if (operation.getContext().suppressGlobalErrorHandling === true) {
    return;
  }

  if (
    captureDataRefreshFeedbackFailure({
      hasGraphQLErrors: Boolean(graphQLErrors?.length),
      hasNetworkError: Boolean(networkError),
    })
  ) {
    return;
  }

  const toast = useToast();
  const suppressOperationToast = operation.getContext().suppressGlobalErrorToast === true;

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, extensions }) => {
      console.error(`[GraphQL error]: ${message}`, extensions);

      // Page-level финансовые flows показывают mapped сообщение сами, чтобы
      // не дублировать toast. Auth/authorization выше всегда обрабатываются тут.
      if (!suppressOperationToast) {
        toast.error(message);
      }
    });
  }

  if (networkError) {
    console.error(`[Network error]:`, networkError);
    if (!suppressOperationToast) {
      toast.error('Ошибка сети. Проверьте подключение к интернету');
    }
  }
});

export const apolloClient = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          bookingGroupAuditLogs: bookingGroupAuditLogsRelayPaginationPolicy,
          clients: clientsRelayPaginationPolicy,
          employees: employeesRelayPaginationPolicy,
          businessLocations: businessLocationsRelayPaginationPolicy,
          loyaltyTransactions: loyaltyTransactionsRelayPaginationPolicy,
          marketingBroadcasts: broadcastsRelayPaginationPolicy,
          marketingBroadcastAudienceEntries: broadcastAudienceEntriesRelayPaginationPolicy,
          platformActivityFeed: platformActivityFeedPolicy,
          platformImpersonationActions: platformImpersonationActionsRelayPaginationPolicy,
          platformImpersonationSessions: platformImpersonationSessionsRelayPaginationPolicy,
          platformOrganizations: platformOrganizationsRelayPaginationPolicy,
          platformUsers: platformUsersRelayPaginationPolicy,
          productMovements: productMovementsRelayPaginationPolicy,
          products: productsRelayPaginationPolicy,
          shifts: shiftsRelayPaginationPolicy,
          paymentJournal: paymentJournalRelayPaginationPolicy,
          showcaseProducts: showcaseProductsRelayPaginationPolicy,
          stockBalances: stockBalancesRelayPaginationPolicy,
          stockMovements: stockMovementsRelayPaginationPolicy,
          tireStorages: tireStoragesRelayPaginationPolicy,
          tireWorkOrders: tireWorkOrdersRelayPaginationPolicy,
          // Messenger: кастомная пагинация (НЕ Relay). Курсорную ленту сообщений
          // и постраничный список групп сливаем вручную с дедупом по id.
          conversationMessages: {
            // Идентичность ленты — диалог + фильтр; beforeMessageId/perPage — пагинация.
            keyArgs: ['conversationId', 'filter'],
            merge(existing, incoming, { readField }) {
              if (!existing) {
                return incoming;
              }
              if (!incoming) {
                return existing;
              }
              // Дедуп по id: докачка старых (beforeMessageId) и WS/оптимистика
              // не задваивают одно сообщение.
              const byId = new Map<string, unknown>();
              for (const ref of existing.messages ?? []) {
                byId.set(readField('id', ref) as string, ref);
              }
              for (const ref of incoming.messages ?? []) {
                byId.set(readField('id', ref) as string, ref);
              }
              // has_more/scheduled_count берём из incoming (актуальны для последней страницы).
              return { ...incoming, messages: Array.from(byId.values()) };
            },
          },
          messengerConversationGroups: {
            // Идентичность списка — поисковый запрос + раздел; page/perPage — пагинация.
            keyArgs: ['search', 'section'],
            merge(existing, incoming, { readField }) {
              if (!existing) {
                return incoming;
              }
              if (!incoming) {
                return existing;
              }
              // meta — inline-объект страницы (не нормализуется), читаем напрямую.
              const incomingMeta = incoming.meta as { current_page?: number } | undefined;
              const incomingPage = incomingMeta?.current_page ?? 1;
              // Страница 1 (или сброс поиска) — свежий список, заменяем.
              if (incomingPage <= 1) {
                return incoming;
              }
              // Страница > 1 — докачка: конкатенация с дедупом групп по client_id.
              const existingGroups = (existing.groups ?? []) as ReadonlyArray<StoreObject>;
              const incomingGroups = (incoming.groups ?? []) as ReadonlyArray<StoreObject>;
              const seen = new Set<string>();
              for (const group of existingGroups) {
                const clientId = readField('client_id', group) as string | null;
                if (clientId) {
                  seen.add(clientId);
                }
              }
              const appended = incomingGroups.filter((group) => {
                const clientId = readField('client_id', group) as string | null;
                return !clientId || !seen.has(clientId);
              });
              return { ...incoming, groups: [...existingGroups, ...appended] };
            },
          },
        },
      },
      MessengerDialogAssignee: { keyFields: false },
      // Группа — derived-агрегат (client_id nullable), не нормализуем; её вложенные
      // last_message/client/conversations нормализуются по id.
      MessengerConversationGroup: { keyFields: false },
      ...authContextTypePolicies,
      ...clientAutomationTypePolicies,
      ...orderTypePolicies,
      ...paymentTypePolicies,
      ...radioTypePolicies,
      ...stockBalanceTypePolicies,
      ...tireServiceTypePolicies,
    },
  }),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
  },
});

setApolloRuntimeClient(apolloClient);
